import api from './api';
import { planFechaToIsoDay } from './visitasPlanMetricsService';

const API_PAGE_SIZE = 20;

const toSafeNumber = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value.trim().replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const normalizePaged = <T,>(data: unknown): { items: T[]; totalPages: number } => {
  const raw = (data || {}) as Record<string, unknown>;
  const items = (raw.items ?? raw.Items ?? []) as T[];
  return {
    items: Array.isArray(items) ? items : [],
    totalPages: Math.max(1, Number(raw.totalPages ?? raw.TotalPages ?? 1)),
  };
};

const buildVendedorQueryParams = (
  nroVendedor: number,
  usuario?: string,
): Record<string, string> => {
  const usuarioTrim = usuario?.trim();
  if (usuarioTrim) return { Usuario: usuarioTrim };
  if (nroVendedor > 0) return { NroVendedor: String(nroVendedor) };
  return {};
};

const fetchAllPages = async <T,>(
  fetchPage: (page: number) => Promise<{ items: T[]; totalPages: number }>,
): Promise<T[]> => {
  const all: T[] = [];
  let page = 1;
  let totalPages = 1;
  do {
    const { items, totalPages: tp } = await fetchPage(page);
    all.push(...items);
    totalPages = tp;
    page += 1;
  } while (page <= totalPages);
  return all;
};

interface ReciboItem {
  nroFormulario?: number;
  NroFormulario?: number;
  nroCliente?: number;
  NroCliente?: number;
  importeTotal?: number;
  ImporteTotal?: number;
  descripcion?: string;
  Descripcion?: string;
  fechaEmision?: string;
  FechaEmision?: string;
  horaEmision?: string;
  HoraEmision?: string;
}

interface PedidoItem {
  nroFormulario?: number;
  NroFormulario?: number;
  nroCliente?: number;
  NroCliente?: number;
  importeTotal?: number;
  ImporteTotal?: number;
  descripcion?: string;
  Descripcion?: string;
  fechaEmision?: string;
  FechaEmision?: string;
  horaEmision?: string;
  HoraEmision?: string;
}

const getNroCliente = (item: { nroCliente?: number; NroCliente?: number }): number =>
  toSafeNumber(item.nroCliente ?? item.NroCliente);

const getNroFormulario = (item: { nroFormulario?: number; NroFormulario?: number }): number =>
  toSafeNumber(item.nroFormulario ?? item.NroFormulario);

const getImporteTotal = (item: { importeTotal?: number; ImporteTotal?: number }): number =>
  toSafeNumber(item.importeTotal ?? item.ImporteTotal);

const getDescripcion = (item: { descripcion?: string; Descripcion?: string }): string =>
  String(item.descripcion ?? item.Descripcion ?? '').trim();

const parseDateTime = (value?: string | null): number | null => {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

const endOfPlanDayMs = (isoDay: string): number => {
  const end = Date.parse(`${isoDay}T23:59:59.999`);
  return Number.isFinite(end) ? end : Date.parse(`${isoDay}T23:59:59`);
};

/** Momento de emisión del comprobante (fecha + hora). */
const getComprobanteEmittedMs = (item: ReciboItem | PedidoItem): number | null => {
  const hora = item.horaEmision ?? item.HoraEmision;
  const horaMs = parseDateTime(hora);
  if (horaMs != null) return horaMs;

  const fecha = item.fechaEmision ?? item.FechaEmision;
  return parseDateTime(fecha);
};

const matchesVisitaEnTexto = (texto: string, idVisita: number): boolean => {
  if (!texto || idVisita <= 0) return false;
  const pattern = new RegExp(`(?:visita|id\\s*visita)[^0-9]*${idVisita}\\b`, 'i');
  return pattern.test(texto);
};

const sortComprobantesPorFormulario = <T extends ReciboItem | PedidoItem>(items: T[]): T[] =>
  [...items].sort((a, b) => getNroFormulario(a) - getNroFormulario(b));

export interface VisitaOperacionMontos {
  cobranzaMonto: number | null;
  pedidoMonto: number | null;
}

export interface VisitaOperacionInput {
  idVisita: number;
  nroCliente: number;
  orden: number;
  fechaHoraInicio?: string | null;
  fechaHoraFin?: string | null;
  fechaHoraOmitida?: string | null;
}

interface VisitaVentana {
  idVisita: number;
  nroCliente: number;
  orden: number;
  inicioMs: number;
  finMs: number;
}

const buildVentanasPorVisita = (
  visitas: VisitaOperacionInput[],
  planIsoDay: string,
): VisitaVentana[] => {
  const sorted = [...visitas].sort(
    (a, b) => a.orden - b.orden || a.idVisita - b.idVisita,
  );
  const dayEndMs = endOfPlanDayMs(planIsoDay);
  const ventanas: VisitaVentana[] = [];

  sorted.forEach((visita, index) => {
    const inicioMs = parseDateTime(visita.fechaHoraInicio);
    if (inicioMs == null) return;

    let finMs =
      parseDateTime(visita.fechaHoraFin) ??
      parseDateTime(visita.fechaHoraOmitida);

    if (finMs == null) {
      for (let j = index + 1; j < sorted.length; j += 1) {
        const siguienteInicio = parseDateTime(sorted[j].fechaHoraInicio);
        if (siguienteInicio != null) {
          finMs = siguienteInicio;
          break;
        }
      }
    }

    if (finMs == null || finMs <= inicioMs) {
      finMs = dayEndMs;
    }

    ventanas.push({
      idVisita: visita.idVisita,
      nroCliente: visita.nroCliente,
      orden: visita.orden,
      inicioMs,
      finMs,
    });
  });

  return ventanas;
};

const comprobanteEnVentana = (
  item: ReciboItem | PedidoItem,
  ventana: VisitaVentana,
): boolean => {
  if (getNroCliente(item) !== ventana.nroCliente) return false;
  const emittedMs = getComprobanteEmittedMs(item);
  if (emittedMs == null) return false;
  return emittedMs >= ventana.inicioMs && emittedMs < ventana.finMs;
};

const toMontoOrNull = (value: number): number | null => (value > 0 ? value : null);

/**
 * Asocia pedidos/recibos a cada visita según cuándo se realizó la visita (inicio → fin),
 * no por cliente del día. Suma todos los comprobantes dentro de esa ventana.
 */
export const allocateMontosPorVisita = (
  visitas: VisitaOperacionInput[],
  recibos: ReciboItem[],
  pedidos: PedidoItem[],
  planIsoDay: string,
): Record<number, VisitaOperacionMontos> => {
  const result: Record<number, VisitaOperacionMontos> = {};
  const visitasSorted = [...visitas].sort(
    (a, b) => a.orden - b.orden || a.idVisita - b.idVisita,
  );

  visitasSorted.forEach((v) => {
    result[v.idVisita] = { cobranzaMonto: null, pedidoMonto: null };
  });

  const ventanas = buildVentanasPorVisita(visitas, planIsoDay);
  const usedReciboIds = new Set<number>();
  const usedPedidoIds = new Set<number>();

  const addCobranza = (idVisita: number, monto: number) => {
    const prev = result[idVisita].cobranzaMonto ?? 0;
    result[idVisita].cobranzaMonto = toMontoOrNull(prev + monto);
  };

  const addPedido = (idVisita: number, monto: number) => {
    const prev = result[idVisita].pedidoMonto ?? 0;
    result[idVisita].pedidoMonto = toMontoOrNull(prev + monto);
  };

  visitasSorted.forEach((visita) => {
    recibos.forEach((recibo) => {
      const nro = getNroFormulario(recibo);
      if (usedReciboIds.has(nro)) return;
      if (
        getNroCliente(recibo) === visita.nroCliente &&
        matchesVisitaEnTexto(getDescripcion(recibo), visita.idVisita)
      ) {
        usedReciboIds.add(nro);
        addCobranza(visita.idVisita, getImporteTotal(recibo));
      }
    });

    pedidos.forEach((pedido) => {
      const nro = getNroFormulario(pedido);
      if (usedPedidoIds.has(nro)) return;
      if (
        getNroCliente(pedido) === visita.nroCliente &&
        matchesVisitaEnTexto(getDescripcion(pedido), visita.idVisita)
      ) {
        usedPedidoIds.add(nro);
        addPedido(visita.idVisita, getImporteTotal(pedido));
      }
    });
  });

  ventanas.forEach((ventana) => {
    recibos.forEach((recibo) => {
      const nro = getNroFormulario(recibo);
      if (usedReciboIds.has(nro) || !comprobanteEnVentana(recibo, ventana)) return;
      usedReciboIds.add(nro);
      addCobranza(ventana.idVisita, getImporteTotal(recibo));
    });

    pedidos.forEach((pedido) => {
      const nro = getNroFormulario(pedido);
      if (usedPedidoIds.has(nro) || !comprobanteEnVentana(pedido, ventana)) return;
      usedPedidoIds.add(nro);
      addPedido(ventana.idVisita, getImporteTotal(pedido));
    });
  });

  const visitasByCliente = new Map<number, VisitaOperacionInput[]>();
  visitasSorted.forEach((v) => {
    const list = visitasByCliente.get(v.nroCliente) ?? [];
    list.push(v);
    visitasByCliente.set(v.nroCliente, list);
  });

  visitasByCliente.forEach((visitasClienteRaw, nroCliente) => {
    const visitasCliente = [...visitasClienteRaw].sort(
      (a, b) => a.orden - b.orden || a.idVisita - b.idVisita,
    );
    const recibosCliente = sortComprobantesPorFormulario(
      recibos.filter(
        (r) => getNroCliente(r) === nroCliente && !usedReciboIds.has(getNroFormulario(r)),
      ),
    );
    const pedidosCliente = sortComprobantesPorFormulario(
      pedidos.filter(
        (p) => getNroCliente(p) === nroCliente && !usedPedidoIds.has(getNroFormulario(p)),
      ),
    );

    let reciboIdx = 0;
    let pedidoIdx = 0;
    visitasCliente.forEach((visita) => {
      if (result[visita.idVisita].cobranzaMonto == null && recibosCliente[reciboIdx]) {
        const recibo = recibosCliente[reciboIdx];
        reciboIdx += 1;
        usedReciboIds.add(getNroFormulario(recibo));
        addCobranza(visita.idVisita, getImporteTotal(recibo));
      }
      if (result[visita.idVisita].pedidoMonto == null && pedidosCliente[pedidoIdx]) {
        const pedido = pedidosCliente[pedidoIdx];
        pedidoIdx += 1;
        usedPedidoIds.add(getNroFormulario(pedido));
        addPedido(visita.idVisita, getImporteTotal(pedido));
      }
    });
  });

  return result;
};

/** Montos de cobranza y pedido por visita (día del plan + vendedor). */
export const loadMontosPorVisitaEnPlan = async (
  planFecha: string,
  nroVendedor: number,
  usuario: string | undefined,
  visitas: VisitaOperacionInput[],
): Promise<Record<number, VisitaOperacionMontos>> => {
  const isoDay = planFechaToIsoDay(planFecha);
  const empty: Record<number, VisitaOperacionMontos> = {};
  visitas.forEach((v) => {
    empty[v.idVisita] = { cobranzaMonto: null, pedidoMonto: null };
  });

  if (!isoDay || visitas.length === 0) return empty;

  const vendedorParams = buildVendedorQueryParams(nroVendedor, usuario);

  try {
    const [recibos, pedidos] = await Promise.all([
      fetchAllPages<ReciboItem>(async (page) => {
        const response = await api.get('/api/Tesoreria/recibos', {
          params: {
            FechaDesde: isoDay,
            FechaHasta: isoDay,
            PageNumber: page,
            PageSize: API_PAGE_SIZE,
            ...vendedorParams,
          },
        });
        return normalizePaged<ReciboItem>(response.data);
      }),
      fetchAllPages<PedidoItem>(async (page) => {
        const response = await api.get('/api/PedidosVenta', {
          params: {
            FechaDesde: isoDay,
            FechaHasta: isoDay,
            PageNumber: page,
            PageSize: API_PAGE_SIZE,
            ...vendedorParams,
          },
        });
        return normalizePaged<PedidoItem>(response.data);
      }),
    ]);

    return allocateMontosPorVisita(visitas, recibos, pedidos, isoDay);
  } catch (error) {
    console.warn('visitasPlanOperaciones: error cargando montos por visita', error);
    return empty;
  }
};
